import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Bar, BarChart,
  AreaChart, Area
} from 'recharts';
import { subscribe, getCached } from '../services/fileDataService';
import '../App.css';

const FileSelect = ({ caption, storageKey, preserveBlank, compact = false, showResult = false, sumsOnly = false, rawTable = false, baseData = [] }) => {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState('');
  const [data, setData] = useState([]);

  // helper: parse minutes from filename like 'NIFTY_3min_r.json' -> 3
  const parseMinutes = (file) => {
    if (!file || typeof file !== 'string') return null;
    const m = file.match(/_(\d+)min(?:_|\.|$)/i);
    if (m && m[1]) return parseInt(m[1], 10);
    return null;
  };

  // helper to parse date strings like '2026-01-30:13:45:00'
  const parseDate = (s) => {
    if (!s) return null;
    // replace first ':' between date and time with space
    const idx = s.indexOf(':');
    if (idx === -1) return new Date(s);
    const t = s.slice(0, idx) + ' ' + s.slice(idx + 1);
    const d = new Date(t);
    return isNaN(d) ? null : d;
  };

  // compute aggregated sums from baseData for a target period row
  const aggregateFromBase = (periodEndDate, targetMinutes) => {
    if (!baseData || !Array.isArray(baseData) || baseData.length === 0) return { sumCE: 0, sumPE: 0 };
    const end = parseDate(periodEndDate);
    if (!end) return { sumCE: 0, sumPE: 0 };
    const start = new Date(end.getTime() - (targetMinutes * 60000));
    let sumCE = 0, sumPE = 0;
    for (const b of baseData) {
      const bd = parseDate(b.date);
      if (!bd) continue;
      if (bd > start && bd <= end) {
        sumCE += Number(b.CEBUY) || 0;
        sumPE += Number(b.PEBUY) || 0;
      }
    }
    return { sumCE, sumPE };
  };

  // helper to compute simple per-row probabilities (used by chart view)
  const computeRow = (row) => {
    const cebuy = Number(row.CEBUY) || 0;
    const cesell = Number(row.CESELL) || 0;
    const pebuy = Number(row.PEBUY) || 0;
    const pesell = Number(row.PESELL) || 0;

    const totalCE = cebuy + cesell;
    const totalPE = pebuy + pesell;

    const pCEBuy = totalCE ? (cebuy / totalCE) : null;
    const pPEBuy = totalPE ? (pebuy / totalPE) : null;

    return { cebuy, cesell, pebuy, pesell, pCEBuy, pPEBuy };
  };

  useEffect(() => {
    fetch('/api/files')
      .then(r => r.json())
      .then(list => {
        setFiles(list);
        if (storageKey) {
          const persisted = localStorage.getItem(storageKey);
          if (persisted && list.includes(persisted)) {
            setSelected(persisted);
            return;
          }
        }
        if (!preserveBlank && list.length > 0) {
          setSelected(list[0]);
        }
      });
  }, [storageKey, preserveBlank]);

  useEffect(() => {
    let unsubscribe = null;
    setData([]);
    if (selected) {
      const cached = getCached(selected);
      if (cached) setData(cached);
      unsubscribe = subscribe(selected, (d) => {
        if (Array.isArray(d)) setData(d);
        else setData([]);
      });
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selected]);

  useEffect(() => {
    if (storageKey && selected) {
      localStorage.setItem(storageKey, selected);
      try {
        const ev = new CustomEvent('buysignal-change', { detail: { key: storageKey, value: selected } });
        window.dispatchEvent(ev);
      } catch (e) {
        // CustomEvent may fail in some environments; ignore safely
      }
    }
  }, [storageKey, selected]);

  return (
    <div className="section">
      {caption && <div className="label">{caption}</div>}
      <select value={selected} onChange={e => setSelected(e.target.value)}>
        {preserveBlank && <option value="">Select file</option>}
        {files.map(f => (
          <option key={f} value={f}>{f}</option>
        ))}
      </select>

      {data.length > 0 && (
        <>
          {!compact && (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={150}>
                <LineChart data={[...data].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" hide={true} />
                  <YAxis />
                  <Tooltip contentStyle={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }} labelStyle={{ color: '#999' }} itemStyle={{ color: '#333' }} />
                  <Legend />
                  <Line type="monotone" dataKey="CE" stroke="#2ecc40" name="CE" dot={false} />
                  <Line type="monotone" dataKey="PE" stroke="#ff4136" name="PE" dot={false} />
                  <Line type="monotone" dataKey="CEOOM" stroke="#005301ff" name="CEOOM" dot={false} />
                  <Line type="monotone" dataKey="PEOOM" stroke="#8c0700ff" name="PEOOM" dot={false} />
                </LineChart>
              </ResponsiveContainer>

              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={[...data].reverse()} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#017407ff" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#21fa24ff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorMA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#cc4040ff" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#871200ff" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <XAxis dataKey="date" />
                  <YAxis />
                  <CartesianGrid strokeDasharray="3 3" />

                  <Tooltip contentStyle={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }} labelStyle={{ color: '#999' }} itemStyle={{ color: '#333' }} />
                  <Legend />

                  <Area type="monotone" dataKey="PE" stroke="#18d61bff" fillOpacity={1} fill="url(#colorActual)" name="PE" />
                  <Area type="monotone" dataKey="PEOOM" stroke="#cc4040ff" fillOpacity={1} fill="url(#colorMA)" name="PEOOM" />
                </AreaChart>
              </ResponsiveContainer>

              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={[...data].reverse()} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorActual2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#017407ff" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#21fa24ff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorMA2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#cc4040ff" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#871200ff" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <XAxis dataKey="date" />
                  <YAxis />
                  <CartesianGrid strokeDasharray="3 3" />
                  <Tooltip contentStyle={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }} labelStyle={{ color: '#999' }} itemStyle={{ color: '#333' }} />
                  <Legend />

                  <Area type="monotone" dataKey="CE" stroke="#18d61bff" fillOpacity={1} fill="url(#colorActual2)" name="CE" />
                  <Area type="monotone" dataKey="CEOOM" stroke="#cc4040ff" fillOpacity={1} fill="url(#colorMA2)" name="CEOOM" />
                </AreaChart>
              </ResponsiveContainer>

              <ResponsiveContainer width="100%" height={100}>
                <BarChart data={[...data].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" hide={true} />
                  <YAxis />

                  <Tooltip contentStyle={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }} labelStyle={{ color: '#999' }} itemStyle={{ color: '#333' }} />
                  <Bar dataKey="CEBUY" stackId="buy" fill="#2ecc40" name="CE Buy" />
                  <Bar dataKey="PESELL" stackId="buy" fill="#005301ff" name="PE Sell" />
                  <Bar dataKey="CESELL" stackId="buy" fill="#ff473dff" name="CE Sell" />
                  <Bar dataKey="PEBUY" stackId="buy" fill="#8c0700ff" name="PE Buy" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                {rawTable ? (
                  <>
                    <th>Price</th>
                    <th>CEBUY</th>
                    <th>CESELL</th>
                    <th>PEBUY</th>
                    <th>PESELL</th>
                  </>
                ) : (
                  <>
                    <th>CEBUY</th>
                    <th>PEBUY</th>
                    {showResult && <th>RESULT</th>}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {[...data].map((row, idx, arr) => {
                const r = computeRow(row);
                const fmt = (v) => (v === null || v === undefined ? '–' : Math.round(v * 100) + '%');
                return (
                  <tr key={row.date} className={idx % 2 === 0 ? "even" : "odd"}>
                    <td>{row.date}</td>
                    {rawTable ? (
                      <>
                        <td>{typeof row.Price === 'number' ? row.Price : (row.Price || '–')}</td>
                        <td>{row.CEBUY !== undefined ? row.CEBUY : '–'}</td>
                        <td>{row.CESELL !== undefined ? row.CESELL : '–'}</td>
                        <td>{row.PEBUY !== undefined ? row.PEBUY : '–'}</td>
                        <td>{row.PESELL !== undefined ? row.PESELL : '–'}</td>
                      </>
                    ) : (showResult || sumsOnly) ? (
                      (() => {
                        // target minutes inferred from selected file name
                        const targetMinutes = parseMinutes(selected) || 1;
                        const { sumCE, sumPE } = aggregateFromBase(row.date, targetMinutes);

                        // previous price (older entry) when rows are chronological
                        const prevRow = arr[idx - 1];
                        const prevPrice = prevRow && typeof prevRow.Price === 'number' ? prevRow.Price : null;
                        const curPrice = typeof row.Price === 'number' ? row.Price : null;

                        let result = 'NEUTRAL';
                        // if (prevPrice !== null && curPrice !== null) {
                        // if (sumCE > 0.5 * sumPE && row.CEBUY > 1) result = 'CEBUY';
                        // else if ( sumPE > 0.5 * sumCE && row.PEBUY > 1) result = 'PEBUY';
                        // else {
                        //   // fallback to simple sum dominance with minimum 1
                        //   if (sumCE > sumPE && sumCE > 1) result = 'CEBUY';
                        //   else if (sumPE > sumCE && sumPE > 1) result = 'PEBUY';
                        // }
                        // }
                        // else {
                        if (sumCE > 0.5 * sumPE && row.CEBUY > 1 ) result = 'CEBUY';
                        else if (sumPE > 0.5 * sumCE && row.PEBUY > 1) result = 'PEBUY';
                        // }

                        const formatSum = (v) => (v || v === 0 ? v : '–');

                        return (
                          <>
                            <td>{formatSum(sumCE)}</td>
                            <td>{formatSum(sumPE)}</td>
                            {showResult && <td style={{ fontWeight: result === 'CEBUY' ? '700' : '400', color: result === 'CEBUY' ? '#2ecc40' : result === 'PEBUY' ? '#ff4136' : '#666' }}>{result}</td>}
                          </>
                        );
                      })()
                    ) : (
                      <>
                        <td>{r.pCEBuy !== null ? fmt(r.pCEBuy) : '–'}</td>
                        <td>{r.pPEBuy !== null ? fmt(r.pPEBuy) : '–'}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default FileSelect;